import 'mapbox-gl/dist/mapbox-gl.css'
import React, { useEffect, useState } from 'react'

import { isWebGLSupported } from './isWebGLSupported'

export const ZipCodeBoundaryStyle = 'mapbox://styles/plovy/cmed40ebx00im01scgxlggzes'

const webGLUnavailableMessage = 'This map requires hardware acceleration (WebGL). Try enabling hardware acceleration in your browser settings to display the map.'
const mapLoadFailedMessage = 'The map could not be loaded. Please refresh the page to try again.'

const isWebGLError = (error?: Error): boolean => !!error && /webgl/i.test(error.message)

export interface IMapBoxContainer {
  mapContainerRef: React.RefObject<HTMLDivElement>
  initializeMap: (container: HTMLDivElement, styleUrl?: string) => Promise<void>
  styleUrl?: string
  isReady?: boolean
  isLoading?: boolean
  hasError?: boolean
  error?: Error
}

export const MapBoxContainer: React.FC<IMapBoxContainer> = ({ mapContainerRef, initializeMap, styleUrl, isReady, isLoading, hasError, error }) => {
  const [webGLSupported] = useState(() => isWebGLSupported())

  useEffect(() => {
    if (webGLSupported && mapContainerRef.current && !isReady && !isLoading && !hasError) {
      const container = mapContainerRef.current

      while (container.firstChild) {
        container.removeChild(container.firstChild)
      }

      initializeMap(container, styleUrl).catch(console.error)
    }
  }, [webGLSupported, mapContainerRef, initializeMap, styleUrl, isReady, isLoading, hasError])

  const containerStyle: React.CSSProperties = {
    backgroundColor: 'lightgrey',
    width: '100%',
    height: '100%',
    position: 'relative'
  }

  const loadingOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    zIndex: 1000
  }

  const messageOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    textAlign: 'center',
    color: '#343741',
    fontSize: '14px',
    lineHeight: 1.5,
    zIndex: 1000
  }

  const showWebGLMessage = !webGLSupported || isWebGLError(error)
  const showLoadFailedMessage = hasError && !showWebGLMessage

  return (
    <div style={containerStyle}>
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      {isLoading && <div style={loadingOverlayStyle}>Loading map...</div>}
      {showWebGLMessage && <div style={messageOverlayStyle}>{webGLUnavailableMessage}</div>}
      {showLoadFailedMessage && <div style={messageOverlayStyle}>{mapLoadFailedMessage}</div>}
    </div>
  )
}
